/**
 * Strings da abertura do caso por serviço (E126, GUS-105, ADR-0088) — namespace `abertura.*`.
 *
 * A mesma abertura serve o portal (banco e escritório) e a área do perito, então nada aqui leva
 * marca: a marca do portal continua só em `pt-BR.portal.ts`. Valem as regras do `pt-BR.ts`:
 * nenhum termo técnico interno e nenhuma string de UI hardcoded em componente.
 */
import { ptBRConferirPorCcb, ptBRContratosDigitais } from './pt-BR.contratosDigitais';
import { ptBROrigemDoCaso } from './pt-BR.origem';

export const ptBRAbertura = {
  eyebrow: 'Cliente / Abertura',
  // E165.F1.T03 (HTML 6.8): o rótulo diz quem abre, e o título é "Enviar documentos", sem subtítulo.
  eyebrowBanco: 'Banco / Abertura',
  eyebrowEscritorio: 'Escritório / Abertura',
  titulo: 'Enviar documentos',
  subtitulo: '',
  voltarAosCasos: 'Voltar aos casos',
  voltar: 'Voltar',
  // E144.F3 (GUS-135): a jornada v6.7.
  // E177.F4 (GUS-253, HTML 6.8 por CCB): Serviço → Contratos → Conferir → Acompanhar.
  passos: {
    ariaLabel: 'Etapas da abertura',
    enviar: 'Serviço',
    contratos: 'Contratos',
    conferir: 'Conferir',
    acompanhar: 'Acompanhar',
  },
  // E165.F1.T03 (GUS-135, HTML 6.8): os nomes do molde; a morfológica aparece e não se escolhe.
  servico: {
    pergunta: 'Tipo de exame',
    eletronica: {
      nome: 'Contrato Digital',
      descricao: '',
    },
    grafotecnica: {
      nome: 'Contrato Não Digital',
      descricao: 'Inclui análise grafotécnica',
    },
    morfologica: {
      nome: 'Análise morfológica de fotografia',
      indisponivel: 'Indisponível por enquanto',
      ariaLabel: 'Análise morfológica de fotografia · indisponível por enquanto',
    },
  },
  // E143.F2 (GUS-140): o banco indica o escritório vinculado que conduz o processo.
  escritorio: {
    titulo: 'Escritório que conduz o processo',
    rotulo: 'Escritório',
    escolha: 'Escolha o escritório',
    ajuda: 'O caso fica com este escritório, que conduz o exame.',
    carregando: 'Carregando os escritórios…',
    erro: 'Não foi possível carregar os escritórios. Recarregue a página.',
    nenhum: 'Nenhum escritório atende o seu banco ainda. Fale com a equipe para fazer o vínculo.',
    obrigatorio: 'Escolha o escritório que conduz o processo.',
  },
  // E177.F1 (GUS-253 A1, ADR-0131): a origem do caso aberto pela operação.
  origem: ptBROrigemDoCaso,
  // E165.F1.T03 (GUS-135, HTML 6.8): processo, pasta e CPF; obrigatórios na abertura do cliente.
  identificacao: {
    titulo: 'Identificação',
    processo: 'Número do processo',
    pasta: 'Número da pasta do banco',
    cpf: 'CPF do cliente',
    opcional: '(opcional)',
    processoObrigatorio: 'Informe o número do processo.',
    processoInvalido: 'Use o formato NNNNNNN-DD.AAAA.J.TR.OOOO.',
    pastaObrigatoria: 'Informe o número da pasta do banco.',
    cpfObrigatorio: 'Informe o CPF do cliente.',
    cpfInvalido: 'Confira o CPF informado.',
  },
  // E144.F3 (GUS-135, ADR-0098 §2.6): o pedido do cliente no processo.
  // E165.F1.T03 (GUS-135/202, HTML 6.8): recolhidos e sem as explicações; a hora saiu na E163 (o
  // que foi gravado fica).
  pedido: {
    prazoTitulo: 'Prazo processual e urgência',
    prazo: 'Prazo processual',
    horaSemPrazo: 'Informe a data do prazo para usar a hora.',
    urgencia: 'Solicitar avaliação de urgência',
    motivo: 'Motivo da urgência',
    motivoObrigatorio: 'Conte o motivo para a equipe avaliar a urgência.',
    observacoesTitulo: 'Observações',
    observacao: 'Observação para a equipe',
  },
  continuar: 'Continuar',
  // E177.F4 (GUS-253): a identificação do cliente incompleta segura o "Continuar" da tela 1.
  semIdentificacao: 'Preencha a identificação para continuar.',
  conferirAcao: 'Conferir',
  gravando: 'Gravando…',
  abrindo: 'Abrindo…',
  erroAoAbrir: 'Não foi possível abrir o caso. Tente novamente.',
  erroAoGravarPedido:
    'Não foi possível gravar a identificação e o pedido. Tente novamente; os arquivos já enviados continuam no caso.',
  erroBancoAtivo: 'Escolha o cliente em que vai trabalhar antes de abrir o caso.',
  erroEscritorioRequerido: 'Escolha o escritório que conduz o processo.',
  erroEscritorioNaoVinculado: 'Este escritório não atende o seu banco. Escolha outro.',
  erroBancoSemEscritorio:
    'Nenhum escritório atende o seu banco ainda. Fale com a equipe para fazer o vínculo.',
  erroServicoDoEscritorio: 'Este serviço é aberto pelo escritório que conduz o processo.',
  arquivos: {
    secaoTitulo: 'Arquivos do caso',
    // E165.F1.T03 (HTML 6.8): a área de soltar, que espera a identificação no envio do cliente.
    arrasteOuSelecione: 'Selecione os documentos',
    // E209.F2.T04 (GUS-296 item 5h): os formatos de `ACCEPT_ELETRONICA`, sem .xls.
    formatos: 'PDF, DOCX, JPG, PNG, ZIP, JSON, TST e registros (XLSX, CSV, TXT, LOG)',
    semIdentificacao: 'Preencha a identificação para enviar documentos.',
    // E133.F2.C01 (GUS-106): só arquivo guardado sem exame não libera o "Conferir".
    soGuardadosTitulo: 'Nenhum arquivo examinável',
    soGuardadosTexto:
      'Os arquivos foram recebidos e continuam guardados no recibo, mas nenhum pode ser examinado. Envie a CCB em PDF para conferir o envio.',
    // A consulta da custódia fica aqui, recolhida, e no caso; saiu do Conferir (GUS-202).
    reciboTitulo: 'Ver recibo e arquivos recebidos',
    reciboTexto: 'Preservação desde o recebimento.',
    verRecibo: 'Ver recibo',
    recebido: 'Arquivo recebido',
    guardadoSemExame: 'Arquivo recebido · guardado sem exame',
    enviando: 'Enviando…',
    remover: 'Remover',
    tentarDeNovo: 'Tentar novamente',
    continuar: 'Continuar',
    // E133.F2 (GUS-106): o arquivo que não foi recebido segura a conferência até ser resolvido.
    // E135.F2.C02: o recibo que não foi fechado também segura.
    falhaPendente:
      'Há arquivo que não foi recebido ou recibo que não foi fechado. Use a ação da linha marcada para continuar.',
    carregando: 'Carregando os arquivos do caso…',
    erroAoCarregar: 'Não foi possível carregar os arquivos do caso. Tente novamente.',
    // E162.F2.T04 (QA-16): o cliente vê o que o arquivo é; o que ele preencheu fica com a equipe.
    comprovanteRecebido: 'Comprovante de formalização recebido',
  },
  // E126.F3: o recibo, com o que o envio já registra por arquivo; o alcance é desde o recebimento.
  custodia: {
    // E144.F3 (GUS-135): a seção da tela Enviar documentos, com o resumo e o recibo expansível.
    secaoTitulo: 'Recebimento e custódia',
    originaisResumo: 'Originais',
    verRecibo: 'Ver recibo, originais e cópias de trabalho',
    titulo: 'Cadeia de custódia',
    alcanceTitulo: 'Custódia desde o recebimento',
    alcanceTexto:
      'Histórico anterior não apresentado. A preservação é demonstrada desde o recebimento.',
    alcanceSelo: 'Desde o recebimento',
    reciboTitulo: 'Recibo do material recebido',
    recebidoEm: 'Recebido em',
    operador: 'Operador',
    originais: 'Arquivos originais',
    hash: 'SHA-256',
    hashUmPorOriginal: 'Um hash por original',
    alcance: 'Alcance demonstrado',
    arvoreTitulo: 'Originais e cópias de trabalho',
    // E134.F2 (GUS-159): "preservado" só depois de a conferência mostrar que o arquivo confere.
    originalLinha: 'Original recebido',
    derivadoLinha: 'Derivado vinculado ao original',
    // F-E126.F3-01: a origem saiu do caso; o derivado não é o arquivo recebido.
    semOrigemLinha: 'Derivado gerado na plataforma · origem indisponível no caso',
    semOrigemReferencia: 'Origem',
    seloSemOrigem: 'Origem indisponível',
    seloHash: 'Hash registrado',
    seloSemHash: 'Sem hash registrado',
    seloDerivado: 'Derivado',
    // E162.F2.T03 (GUS-135): os originais à vista; as cópias de trabalho de cada um por expansão.
    verCopias: 'Ver cópias de trabalho ({n})',
    naoRegistrado: 'Não registrado',
    // E133.F2 (GUS-106, ADR-0089): o recibo por envio, com o que não é examinado.
    caso: 'Caso',
    arquivosRecebidos: 'Arquivos recebidos',
    contagem: '{n} arquivo(s)',
    contagemComNaoExaminados: '{n} arquivo(s), {m} guardado(s) sem exame',
    recebimentosTitulo: 'Recebimentos',
    recebimentoNumero: 'Recebimento {n}',
    recebimentoSemRegistro: 'Recebido antes do registro por envio',
    recebimentoId: 'ID {id}',
    naoExaminadoLinha: 'Recebido e guardado · sem exame: {motivo}',
    seloNaoExaminado: 'Sem exame',
    // E134.F2 (GUS-159, ADR-0090): a situação conferida de cada arquivo e a conferência na tela.
    situacao: {
      confere: 'preservado · conferido em {data}',
      diverge: 'o arquivo guardado não reproduz o hash do recebimento · conferido em {data}',
      ausente: 'o arquivo guardado não foi encontrado · conferido em {data}',
      sem_registro: 'recebido antes do registro do hash · conferido em {data}',
      naoConferido: 'ainda não conferido',
    },
    selo: {
      confere: 'Conferido',
      diverge: 'Diverge do registro',
      ausente: 'Arquivo ausente',
      sem_registro: 'Sem hash registrado',
    },
    transformacao: {
      membro_do_pacote: 'Arquivo de dentro do pacote, sem alteração',
      paginas_separadas: 'Páginas {paginas} separadas do original',
      imagem_da_pagina: 'Imagem da página {paginas} do original',
    },
    conferenciaTitulo: 'Conferência de integridade',
    conferenciaExplica:
      'A conferência lê cada arquivo guardado do caso e refaz o SHA-256 registrado no recebimento.',
    conferenciaNunca: 'Os arquivos deste caso ainda não foram conferidos.',
    conferenciaUltima: 'Última conferência',
    conferenciaNaLeitura: 'registrada na entrega de um arquivo',
    conferenciaResultado: '{conferem} de {total} arquivo(s) conferem',
    conferenciaDivergem: '{n} divergem do registro',
    conferenciaAusentes: '{n} não encontrado(s)',
    conferenciaSemRegistro: '{n} sem hash registrado',
    conferenciaAlertaTitulo: 'Arquivo guardado diverge do registro',
    conferenciaAlertaTexto:
      'O uso desse arquivo está interrompido e a ocorrência ficou registrada abaixo. O histórico das conferências é mantido.',
    // E136.F3 (GUS-152, ADR-0092): as ocorrências de custódia e a resolução de cada uma.
    ocorrencias: {
      titulo: 'Ocorrências de custódia',
      explica:
        'Arquivo que diverge do registro do recebimento ou não foi encontrado abre uma ocorrência. Enquanto ela não for resolvida com o restauro, o arquivo e o que depende dele ficam fora do exame; os demais seguem.',
      nenhuma: 'Nenhuma ocorrência registrada.',
      aberta: 'Aberta',
      resolvida: 'Resolvida',
      semNome: {
        documento: 'Documento que não está mais no caso',
        nao_examinado: 'Arquivo guardado sem exame',
        foto: 'Fotografia do signatário',
      },
      situacao: {
        diverge: 'O arquivo guardado não reproduz o hash do recebimento',
        ausente: 'O arquivo guardado não foi encontrado',
      },
      origem: {
        tela: 'na conferência de integridade',
        leitura: 'na leitura do arquivo',
        operacao: 'na verificação da operação',
      },
      abertaEm: 'Aberta em {data}, {origem}',
      abertaPor: 'pedida por {quem}',
      esperado: 'Hash do recebimento',
      obtido: 'Hash lido agora',
      acao: {
        restaurado: 'Restaurado',
        retirado_do_exame: 'Retirado do exame',
        reenviado_pelo_banco: 'Reenviado pelo banco',
      },
      acaoExplica: {
        restaurado:
          'O arquivo voltou ao estado recebido. Ele é conferido de novo antes de a resolução ser registrada.',
        retirado_do_exame: 'O caso segue sem este arquivo. Ele continua guardado, fora do exame.',
        reenviado_pelo_banco:
          'O banco mandou o arquivo de novo. O reenvio entra como arquivo novo; este fica fora do exame.',
      },
      resolvidaEm: '{acao} em {data}',
      resolvidaPor: 'por {quem}',
      resolver: 'Resolver',
      escolha: 'Como a ocorrência foi resolvida',
      nota: 'Observação (opcional)',
      confirmar: 'Registrar resolução',
      cancelar: 'Cancelar',
      registrando: 'Registrando…',
      erros: {
        sem_restauro:
          'O arquivo ainda não confere com o registro do recebimento. A conferência ficou registrada e a ocorrência continua aberta.',
        ja_resolvida: 'Esta ocorrência já foi resolvida.',
        sem_permissao: 'A resolução é de quem atende o caso.',
        nao_encontrada: 'Ocorrência não encontrada neste caso.',
        indisponivel: 'Não foi possível registrar a resolução. Tente novamente.',
      },
    },
    // E135.F2 (GUS-158, ADR-0091): o manifesto selado de cada recebimento e a conferência por fora.
    manifesto: {
      linha: 'Manifesto {id} · selado em {data}',
      aberto: 'Recebimento em aberto: o manifesto é selado ao fim do envio.',
      semAncora:
        'Sem assinatura nem carimbo do tempo: nenhum perfil aprovado. A conferência é pelo SHA-256.',
      complemento: 'Complemento do manifesto {id}',
      baixar: 'Baixar manifesto',
      baixando: 'Baixando…',
      erroBaixar: 'Não foi possível baixar o manifesto. Tente novamente.',
      arquivos: 'Arquivos recebidos neste envio ({n})',
      naoLido: 'não lido',
      situacao: {
        pacote: 'Pacote aberto',
        examinado: 'Examinado',
        nao_examinado: 'Guardado sem exame',
        copia_identica: 'Cópia de arquivo já recebido',
        ruido_do_sistema: 'Arquivo do sistema operacional',
      },
      comoConferirTitulo: 'Como conferir por fora',
      comoConferirPassos: [
        'Baixe o manifesto do recebimento.',
        'Calcule o SHA-256 do arquivo baixado (por exemplo, sha256sum) e compare com o SHA-256 do manifesto mostrado aqui.',
        'Para cada arquivo que você tem, calcule o SHA-256 e compare com o da linha dele no manifesto.',
        'O manifesto não leva assinatura nem carimbo do tempo: a conferência é pelo hash.',
      ],
    },
    conferir: 'Conferir integridade',
    conferindo: 'Conferindo os arquivos…',
    conferirErro: 'Não foi possível conferir os arquivos agora. Tente novamente.',
    conferirSemPermissao: 'A conferência é feita por quem atende o caso.',
    carregando: 'Carregando o recibo…',
    erro: 'Não foi possível carregar o recibo. Tente novamente.',
    // O selo do juízo da custódia na consulta "Cadeia de custódia" (veio do Conferir na E165).
    seloDaCadeia: {
      aderente: 'Aderente com limitações',
      semConferencia: 'Integridade ainda não conferida',
      divergencia: 'Divergência na conferência',
    },
    // E163.F3 (GUS-209 A67-13/14): a consulta "Cadeia de custódia" em destino próprio, como no molde.
    paginaEyebrow: 'Material / Custódia',
    paginaTitulo: 'Cadeia de custódia',
    paginaSubtitulo: 'Recibo, integridade e origem dos arquivos.',
    comoComplementarTitulo: 'Como complementar a origem',
    comoComplementarTexto:
      'Se disponíveis, envie o termo de extração ou os registros de origem em Enviar documentos. O envio pode continuar com essa limitação.',
  },
  // E177.F4 (GUS-253, ADR-0130; HTML 6.8 por CCB): a tela 2 do Contrato Digital, um bloco por CCB.
  contratosDigitais: ptBRContratosDigitais,
  // E177.F5 (GUS-253): o Conferir e o Acompanhar por CCB declarada.
  conferirPorCcb: ptBRConferirPorCcb,
  // E144.F4 (GUS-105 + GUS-135, ADR-0098): Conferir envio, uma linha por CCB, e o recibo dos casos.
  conferirEnvio: {
    titulo: 'Conferir envio',
    // E165.F1.T05 (HTML 6.8): sem subtítulo.
    subtitulo: '',
    identificadas: '{n} CCBs identificadas',
    identificadaUma: '1 CCB identificada',
    semContrato: 'Material sem CCB identificada',
    semContratoTexto:
      'Nenhum arquivo foi reconhecido como CCB. O envio segue como um caso, e a equipe confere o material.',
    // E165.F1.T05 (GUS-105, HTML 6.8): a tabela CCB / Exame / Destino e a linha de um caso por CCB.
    colunaCcb: 'CCB',
    colunaExame: 'Exame',
    colunaDestino: 'Destino',
    apoiosVinculados: 'Documentos de apoio vinculados à CCB',
    destino: {
      novo: 'Novo caso',
      complemento: 'Caso {codigo} · Aberto',
      // E165.F1.T06 (ADR-0114 §2): o caso com o laudo final assinado ou encerrado.
      complementoFechado: 'Caso {codigo} · Fechado',
      ja_recebido: 'Já recebida no caso {codigo}',
      caso_do_escritorio: 'Caso {codigo}, do escritório',
    },
    // E165.F1.T06 (GUS-105, HTML 6.8): a escolha explícita para a CCB que já tem caso.
    escolha: {
      legendaAberto: '{ccb}: caso aberto {codigo}',
      legendaFechado: '{ccb}: caso fechado {codigo}',
      perguntaAberto: 'Deseja vincular ao caso existente ou abrir um novo caso?',
      // E177.F2 (GUS-253 A2): a CCB enviada de novo, com os mesmos arquivos, também pergunta.
      perguntaJaRecebida:
        'Os mesmos arquivos já estão neste caso. Deseja vincular ao caso existente ou abrir um novo caso?',
      perguntaFechado: 'Deseja uma nova análise com documentos adicionais?',
      link: 'Vincular ao caso existente',
      new: 'Abrir novo caso para esta CCB',
      newFechado: 'Abrir nova análise com documentos adicionais',
      consultar: 'Consultar caso existente',
    },
    erroEscolha:
      'Uma CCB deste envio já tem caso. Escolha o que fazer com ela para concluir o envio.',
    resumoUma: '1 CCB, um caso e um exame.',
    resumoVarias: '{n} CCBs, com um caso e um exame para cada uma.',
    material: '{n} arquivo(s)',
    materialComApoio: '{n} arquivo(s) · {m} de apoio',
    fonte: {
      ade: 'ADE',
      numero_da_cedula: 'CCB',
      sistema: 'Contrato',
      proposta: 'Proposta',
      envelope: 'Envelope',
      dossie: 'Dossiê',
      numero_do_contrato: 'Contrato',
      proposta_do_manifesto: 'Proposta',
    },
    pendencia: {
      separacao: 'Mais de uma CCB no mesmo arquivo: a operação separa.',
    },
    // E162.F2.T04: o caso que já tem a CCB se abre daqui.
    abrirCaso: 'Abrir o caso {codigo}',
    // E163.F3 (GUS-209 A67-15): o aviso curto do molde no lugar de "Ao concluir o envio".
    casoEncontrado: {
      titulo: 'Caso encontrado',
      tituloVarios: 'Casos encontrados',
      complemento:
        '{ccb} já possui um caso ({codigo}). Os novos documentos serão adicionados a ele.',
      ja_recebido: '{ccb} já foi recebida no caso {codigo}, com os mesmos arquivos.',
      caso_do_escritorio:
        '{ccb} já está no caso {codigo}, aberto pelo escritório. O envio desta CCB fica com ele.',
      nenhumNovo: 'Nenhum caso novo será aberto neste envio.',
    },
    verArquivos: 'Ver arquivos e identificação',
    rotuloProcesso: 'Processo',
    rotuloPasta: 'Pasta do banco',
    rotuloCpf: 'CPF',
    naoInformado: 'Não informado',
    recebido: 'Recebido',
    // E165.F1.T04 (GUS-135, ADR-0114 §1): a conferência do CPF informado contra o material. Só a
    // divergência trava; o não conferido segue, e a equipe confere.
    cpf: {
      titulo: 'Conferência do CPF',
      compativel: 'CPF compatível com os documentos',
      divergenteTitulo: 'CPF informado diverge do material',
      divergenteTexto: 'Corrija o CPF ou substitua os documentos para continuar.',
      corrigir: 'Corrigir identificação ou documentos',
      naoConferidoTitulo: 'CPF ainda não conferido',
      naoConferidoTexto: 'A equipe confere o CPF com os documentos depois do envio.',
    },
    perguntaTitulo: 'A qual CCB este arquivo pertence?',
    perguntaTexto: 'Não deu para saber pelo arquivo. A resposta decide em qual caso ele entra.',
    todas: 'Vale para todas as CCBs',
    outroContrato: 'É outra CCB',
    perguntaPendente: 'Responda a qual CCB cada arquivo pertence para concluir.',
    verificando: 'Verificação em andamento',
    verificandoTexto:
      'Os arquivos ainda estão sendo lidos. A conferência se atualiza sozinha quando terminar.',
    concluir: 'Concluir envio',
    concluindo: 'Concluindo…',
    erro: 'Não foi possível concluir o envio. Tente novamente.',
    erroLendo: 'Os arquivos ainda estão sendo lidos. Aguarde a verificação terminar.',
    erroSemArquivo:
      'Nenhum arquivo do envio pode ser examinado. Os arquivos recebidos continuam guardados no recibo; volte e envie a CCB em PDF.',
    semExaminavelTitulo: 'Nenhum arquivo examinável',
    semExaminavelTexto:
      'Os arquivos foram recebidos e continuam guardados no recibo, mas nenhum pode ser examinado. Volte e envie a CCB em PDF para concluir.',
    erroIdentificacao:
      'Falta identificação: volte e preencha o número do processo, a pasta do banco e o CPF do cliente.',
    erroCpfDivergente:
      'O CPF informado diverge do material. Corrija a identificação ou substitua os documentos.',
    carregando: 'Carregando a conferência…',
    erroAoCarregar: 'Não foi possível carregar a conferência. Tente novamente.',
  },
  acompanhar: {
    // E165.F1.T07 (HTML 6.8): "Envio recebido", sem subtítulo; o título não diz "caso aberto"
    // quando o envio só vinculou (ADR-0114 §4).
    titulo: 'Envio recebido',
    subtitulo: '',
    cardTitulo: 'Acompanhar',
    // A linha de cada CCB depois do exame: a nova análise com a origem, ou o caso que já existia.
    linha: {
      novaAnalise: 'Nova análise, origem {codigo}',
      complemento: 'Vinculado ao caso existente',
      ja_recebido: 'Já recebida neste caso',
      caso_do_escritorio: 'Caso do escritório',
    },
    abrirCaso: 'Abrir caso',
    voltarAosCasos: 'Voltar aos casos',
    enviarDocumentos: 'Enviar documentos',
    naoConcluido: 'Este envio ainda não foi concluído.',
    irConferir: 'Conferir o envio',
    carregando: 'Carregando os casos do envio…',
    erro: 'Não foi possível carregar os casos do envio. Tente novamente.',
  },
  // E126.F4: o processo com os contratos e exames dele, e mais um exame sem recriar o caso.
  processo: {
    pasta: 'Pasta no banco',
    numero: 'Número do processo',
    numeroAusente: 'Ainda não informado',
    carregando: 'Carregando o processo…',
    erro: 'Não foi possível carregar o processo. Tente novamente.',
    noProcesso: 'Novo exame no processo',
    noProcessoTexto: 'A pasta e o número vêm do processo. Escolha o serviço e envie os arquivos.',
    semPasta: 'Sem pasta',
  },
} as const;
